import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { usePaymentMethods } from '../hooks/usePayments';

const { FiX, FiSave, FiLoader, FiDollarSign } = FiIcons;

const PaymentModal = ({ isOpen, onClose, payment = null, onSubmit, invoices = [] }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { methods: paymentMethods } = usePaymentMethods();
  
  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    defaultValues: {
      invoice_id: '',
      paid_amount: 0,
      payment_method: 'bank_transfer',
      payment_date: new Date().toISOString().split('T')[0],
      transaction_id: '',
      payment_notes: '',
      late_fee: 0
    }
  });

  const watchedInvoiceId = watch('invoice_id');
  const watchedPaidAmount = watch('paid_amount');

  // Get selected invoice details
  const selectedInvoice = invoices.find(inv => inv.id.toString() === watchedInvoiceId);
  const invoiceAmount = selectedInvoice?.total_amount || 0;
  const remainingBalance = invoiceAmount - (watchedPaidAmount || 0);

  // Determine payment status
  const getPaymentStatus = () => {
    if (!watchedPaidAmount || watchedPaidAmount <= 0) return 'pending';
    if (watchedPaidAmount >= invoiceAmount) return 'paid';
    return 'partial';
  };

  useEffect(() => {
    if (payment) {
      reset({
        invoice_id: payment.invoice_id?.toString() || '',
        paid_amount: payment.paid_amount || 0,
        payment_method: payment.payment_method || 'bank_transfer',
        payment_date: payment.payment_date || new Date().toISOString().split('T')[0],
        transaction_id: payment.transaction_id || '',
        payment_notes: payment.payment_notes || '',
        late_fee: payment.late_fee || 0
      });
    } else {
      reset({
        invoice_id: '',
        paid_amount: 0,
        payment_method: 'bank_transfer',
        payment_date: new Date().toISOString().split('T')[0],
        transaction_id: '',
        payment_notes: '',
        late_fee: 0
      });
    }
  }, [payment, reset]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const paymentStatus = getPaymentStatus();
      const invoice = invoices.find(inv => inv.id.toString() === data.invoice_id);
      
      await onSubmit({
        ...data,
        invoice_id: parseInt(data.invoice_id),
        paid_amount: parseFloat(data.paid_amount),
        late_fee: parseFloat(data.late_fee || 0),
        amount: invoice?.total_amount || 0,
        remaining_balance: Math.max(0, (invoice?.total_amount || 0) - parseFloat(data.paid_amount)),
        payment_status: paymentStatus,
        invoice_number: invoice?.invoice_number,
        client_id: invoice?.client_id,
        client: invoice?.client
      });
      onClose();
    } catch (error) {
      console.error('Error submitting payment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {payment ? 'Edit Payment' : 'Record New Payment'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {payment ? 'Update payment details' : 'Record a payment received from client'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  disabled={isSubmitting}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-6">
              {/* Invoice Selection */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiFileText} className="w-5 h-5 mr-2" />
                  Invoice Details
                </h3>
                
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Invoice *
                    </label>
                    <select
                      {...register('invoice_id', { required: 'Invoice selection is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={!!payment}
                    >
                      <option value="">Choose an invoice</option>
                      {invoices.map(invoice => (
                        <option key={invoice.id} value={invoice.id}>
                          {invoice.invoice_number} - {invoice.client?.business_name} - {formatCurrency(invoice.total_amount)}
                        </option>
                      ))}
                    </select>
                    {errors.invoice_id && (
                      <p className="text-red-500 text-sm mt-1">{errors.invoice_id.message}</p>
                    )}
                  </div>

                  {selectedInvoice && (
                    <div className="mt-4 p-4 bg-white rounded-lg border border-blue-300">
                      <h4 className="font-semibold text-gray-700 mb-2">Invoice Information</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p><span className="font-medium">Client:</span> {selectedInvoice.client?.business_name}</p>
                          <p><span className="font-medium">Invoice #:</span> {selectedInvoice.invoice_number}</p>
                        </div>
                        <div>
                          <p><span className="font-medium">Total Amount:</span> {formatCurrency(selectedInvoice.total_amount)}</p>
                          <p><span className="font-medium">Due Date:</span> {new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2" />
                  Payment Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Amount * {selectedInvoice && `(Max: ${formatCurrency(selectedInvoice.total_amount)})`}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('paid_amount', { 
                        required: 'Payment amount is required',
                        min: { value: 0.01, message: 'Amount must be greater than 0' },
                        max: selectedInvoice ? { 
                          value: selectedInvoice.total_amount, 
                          message: 'Amount cannot exceed invoice total' 
                        } : undefined
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                    {errors.paid_amount && (
                      <p className="text-red-500 text-sm mt-1">{errors.paid_amount.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Method *
                    </label>
                    <select
                      {...register('payment_method', { required: 'Payment method is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </select>
                    {errors.payment_method && (
                      <p className="text-red-500 text-sm mt-1">{errors.payment_method.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      {...register('payment_date', { required: 'Payment date is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    {errors.payment_date && (
                      <p className="text-red-500 text-sm mt-1">{errors.payment_date.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction ID
                    </label>
                    <input
                      type="text"
                      {...register('transaction_id')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter transaction reference"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Late Fee
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('late_fee', { min: { value: 0, message: 'Late fee cannot be negative' } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                    {errors.late_fee && (
                      <p className="text-red-500 text-sm mt-1">{errors.late_fee.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Notes
                    </label>
                    <textarea
                      {...register('payment_notes')}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Additional notes about this payment..."
                    />
                  </div>
                </div>

                {/* Payment Summary */}
                {selectedInvoice && watchedPaidAmount > 0 && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-green-300">
                    <h4 className="font-semibold text-gray-700 mb-2">Payment Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Invoice Amount:</span>
                        <span className="font-medium">{formatCurrency(invoiceAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payment Amount:</span>
                        <span className="font-medium">{formatCurrency(watchedPaidAmount)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span>Remaining Balance:</span>
                        <span className={`font-bold ${remainingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {formatCurrency(remainingBalance)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payment Status:</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          getPaymentStatus() === 'paid' 
                            ? 'bg-green-100 text-green-800' 
                            : getPaymentStatus() === 'partial'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {getPaymentStatus().charAt(0).toUpperCase() + getPaymentStatus().slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{payment ? 'Update Payment' : 'Record Payment'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;